import React, { useCallback, useEffect, useRef, useState } from 'react';

type CameraFacing = 'environment' | 'user';

export type CameraPermissionIssue =
  | 'denied'
  | 'not_found'
  | 'in_use'
  | 'insecure'
  | 'unsupported'
  | 'unknown';

function classifyCameraError(err: unknown): CameraPermissionIssue {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure';
  const name = (err as { name?: string })?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'denied';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'not_found';
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return 'in_use';
  }
  if (name === 'SecurityError') return 'insecure';
  if (name === 'TypeError') return 'unsupported';
  return 'unknown';
}

export function cameraIssueMessage(issue: CameraPermissionIssue): string {
  switch (issue) {
    case 'denied':
      return 'Camera permission was blocked. Enable camera access for this site in your browser or device settings, then try again. You can also use “Use device camera” below.';
    case 'not_found':
      return 'No camera was found on this device. Use “Choose from library” or connect a camera.';
    case 'in_use':
      return 'The camera is busy in another app. Close other camera apps and try again, or use “Use device camera”.';
    case 'insecure':
      return 'Camera access needs a secure connection (HTTPS or localhost). Use “Use device camera” or upload from your library.';
    case 'unsupported':
      return 'This browser can’t open the live camera. Use “Use device camera” or choose photos from your library.';
    default:
      return 'Couldn’t open the camera. Try “Use device camera” or choose photos from your library.';
  }
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => {
    try {
      t.stop();
    } catch {
      /* ignore */
    }
  });
}

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  /** Opens native camera / file picker as fallback */
  onUseNativeCamera: () => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({
  onCapture,
  onClose,
  onUseNativeCamera,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [facing, setFacing] = useState<CameraFacing>('environment');
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(true);
  const [issue, setIssue] = useState<CameraPermissionIssue | null>(null);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(null);
  }, []);

  const attachStream = useCallback(async (nextFacing: CameraFacing) => {
    setStarting(true);
    setReady(false);
    setIssue(null);

    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setIssue('insecure');
      setStarting(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setIssue('unsupported');
      setStarting(false);
      return;
    }

    stopStream(streamRef.current);
    streamRef.current = null;

    const tryConstraints = async (constraints: MediaStreamConstraints) => {
      return navigator.mediaDevices.getUserMedia(constraints);
    };

    try {
      let stream: MediaStream;
      try {
        stream = await tryConstraints({
          audio: false,
          video: {
            facingMode: { ideal: nextFacing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
      } catch (firstErr) {
        // Some desktops reject facingMode; retry with any camera
        stream = await tryConstraints({
          audio: false,
          video: true,
        });
        void firstErr;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        await video.play().catch(() => undefined);
      }
      setReady(true);
    } catch (err) {
      console.warn('Camera start failed', err);
      setIssue(classifyCameraError(err));
      setReady(false);
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    void attachStream(facing);
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [facing, attachStream]);

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    if (!video.videoWidth || !video.videoHeight) {
      setIssue('unknown');
      setCaptureBusy(false);
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas unsupported');
      ctx.drawImage(video, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
      });
      if (!blob) throw new Error('Capture failed');

      const file = new File([blob], `capture-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      onCapture(file);
    } catch (err) {
      console.error(err);
      setIssue('unknown');
    } finally {
      setCaptureBusy(false);
    }
  };

  const handleShutter = () => {
    if (!ready || captureBusy) return;

    setCaptureBusy(true);
    if (!timerEnabled) {
      void capturePhoto();
      return;
    }

    setCountdown(3);
    let remaining = 3;
    countdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setCountdown(remaining);
        return;
      }

      clearCountdown();
      void capturePhoto();
    }, 1000);
  };

  const flipCamera = () => {
    setFacing((f) => (f === 'environment' ? 'user' : 'environment'));
  };

  return (
    <div className="cd-camera">
      <div className="cd-camera-viewport">
        <video
          ref={videoRef}
          className="cd-camera-video"
          playsInline
          muted
          autoPlay
        />
        {starting && <div className="cd-camera-status">Starting camera…</div>}
        {countdown !== null && (
          <div className="cd-camera-countdown" role="status" aria-live="assertive">
            {countdown}
          </div>
        )}
        {issue && !starting && (
          <div className="cd-camera-status cd-camera-status-error">
            <p>{cameraIssueMessage(issue)}</p>
            <div className="cd-camera-fallback-actions">
              <button type="button" className="cd-btn cd-btn-primary" onClick={onUseNativeCamera}>
                Use device camera
              </button>
              <button
                type="button"
                className="cd-btn"
                onClick={() => void attachStream(facing)}
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="cd-camera-controls">
        <button type="button" className="cd-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="cd-camera-shutter"
          onClick={handleShutter}
          disabled={!ready || !!issue || captureBusy}
          aria-label="Take photo"
        />
        <button
          type="button"
          className="cd-btn"
          onClick={flipCamera}
          disabled={starting || (!!issue && issue !== 'unknown')}
          title="Flip camera"
        >
          Flip
        </button>
      </div>

      <label className="cd-camera-timer">
        <input
          type="checkbox"
          checked={timerEnabled}
          onChange={(event) => setTimerEnabled(event.target.checked)}
          disabled={captureBusy}
        />
        <span>3s timer</span>
      </label>

      <p className="cd-camera-hint">
        Tip: on phones, “Use device camera” opens the system camera if the live preview isn’t available.
      </p>
    </div>
  );
};

export default CameraCapture;
